import React, { useEffect, useRef, useMemo } from "react";
import { Box, Button, Card, Typography, CheckboxField, Stack, DateField } from "@curves/ui-base";
import { Grid } from "@mui/material";
import useMessageDialog from "@util/hook/useMessageDialog";
import { refIsRequiredError, getErrorModuleStatus } from "@util/utils";
import { useCyclePurchaseEffectOrderData, useCyclePurchaseEffectOrderSubmit, useCyclePurchaseEffectRange, useEffectTypeSelection, useHCProductData } from "@icoach/healthyCyclePurchase/hooks";
import { TermSection, CyclePurchaseSummaryCard, PaymentSection, InvoiceSection, ReceiverSection, PickItem, SelectWithMenuItems, CartTable } from "@icoach/healthyCyclePurchase/components";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import { EmployeeSelect, FocusModeToggle } from "@icoach/components";
import { getNowTime, initialDate, isValidDate } from "@util/moment";
import { EFFECT_CONTENT_TYPE } from "@constants/";
import { isPriceNoticePeriod } from "@icoach/healthyCyclePurchase/CyclePurchase.util";

/**
 * 健康產品變更單頁面
 *
 * 功能說明：
 * - 僅處理訂單內容變更（商品、出貨、收件人、付款、發票）
 * - 統一管理 effectType 選取狀態，資料流清晰
 */
const CyclePurchaseChangePage = props => {
	const setMessageDialog = useMessageDialog();
	const { cycleOrderID, effectID = 0 } = props.match.params;

	// 使用 hooks 管理資料
	const { cycleOrder, effectOrder, resource, memberID, clubID, shippingDay } = useCyclePurchaseEffectOrderData({
		cycleOrderID,
		effectID,
	});

	// 提交變更單 hook
	const {
		submit,
		isSubmitting: isSubmittingOrder,
		validationErrors,
	} = useCyclePurchaseEffectOrderSubmit({
		effectID,
		memberID,
	});

	// 使用 hook 取得變更單的生效日期和下次出貨日期
	const { effectDate, nextShippingDate, getEffectRangeDebounced } = useCyclePurchaseEffectRange();

	// 管理 effectType 選取狀態 Hook
	const { hasAnySelected, handleEffectTypeChange, isEffectTypeSelected, getSelectedEffectTypesArray, selectedEffectTypesArray } = useEffectTypeSelection({
		initialSelected: effectOrder?.effectType || [],
	});

	// 管理商品資料 Hook
	const products = useHCProductData();

	// pickItem refs
	const productPickItemRef = useRef(null);
	const shippingPickItemRef = useRef(null);
	const receiverPickItemRef = useRef(null);
	const paymentPickItemRef = useRef(null);
	const invoicePickItemRef = useRef(null);

	// refs
	const productItemsRef = useRef(null);
	const productConfirmRef = useRef(null);
	const shippingDayRef = useRef(null);
	const receiverRef = useRef(null);
	const paymentRef = useRef(null);
	const invoiceRef = useRef(null);
	const termRef = useRef(null);
	const effectCreateDateRef = useRef(null);
	const effectDateRef = useRef(null);
	const employeeRef = useRef(null);

	// 聚焦模式 ref
	const focusModeRef = useRef(false);

	// 啟用無操作計時器
	useInactivityTimerToggle();

	// 根據 effectID 決定預設值來源：effectID = 0 使用 cycleOrder，否則使用 effectOrder
	const defaultSource = useMemo(() => {
		const isNewEffectOrder = Number(effectID) === 0;
		return isNewEffectOrder ? cycleOrder : effectOrder;
	}, [effectID, cycleOrder, effectOrder]);

	// 處理出貨週期變更，重新計算 effectDate 和 nextShippingDate
	const handleShippingDayChange = (e, n, val) => {
		if (val) {
			const params = getParams();
			getEffectRangeDebounced({
				...params,
				shippingDay: val,
			});
		}
	};

	// 變更 effectDate 時，重新計算 effectDate 和 nextShippingDate
	const handleEffectDateChange = (_, date) => {
		if (isValidDate(date)) {
			const params = getParams();
			getEffectRangeDebounced({
				...params,
				effectOrder: date,
			});
		}
	};

	// 檢查商品錯誤
	const checkProductError = () => {
		let isError = refIsRequiredError(productItemsRef, productConfirmRef);
		let items = productItemsRef.current?.getResult();
		if (Array.isArray(items) && items.length < 1) {
			if (!isError) isError = true;
			setMessageDialog({
				open: true,
				title: "提醒",
				msg: "至少要購買一項商品才可以送單喔，謝謝。",
			});
		}
		return isError;
	};

	const getParams = () => {
		let result = {
			effectType: getSelectedEffectTypesArray(),
			effectDate,
			shippingDay,
			clubID,
			memberID,
			cycleOrderID,
		};

		// 商品資料
		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.ITEMS)) {
			if (productItemsRef.current && productItemsRef.current.getResult) {
				result.items = productItemsRef.current.getResult();
			}
		}

		// 出貨資料
		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.SHIPPINGDAY)) {
			if (shippingDayRef.current && shippingDayRef.current.getResult) {
				result.shippingDay = shippingDayRef.current.getResult() || 0;
			}
		}

		// 收件人資料
		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.RECEIVER)) {
			if (receiverRef.current && receiverRef.current.getResult) {
				Object.assign(result, receiverRef.current.getResult());
			}
		}

		// 付款資料
		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.PAYMENT)) {
			if (paymentRef.current && paymentRef.current.getResult) {
				Object.assign(result, paymentRef.current.getResult());
			}
		}

		// 發票資料
		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.INVOICE)) {
			if (invoiceRef.current && invoiceRef.current.getResult) {
				Object.assign(result, invoiceRef.current.getResult());
			}
		}

		if (termRef.current && termRef.current.getResult) {
			result = Object.assign(result, termRef.current.getResult());
		}

		if (effectCreateDateRef.current?.getResult) {
			result.effectCreateDate = effectCreateDateRef.current.getResult();
		}

		if (effectDateRef.current?.getResult) {
			result.effectDate = effectDateRef.current.getResult();
		}

		if (employeeRef.current?.getResult) {
			result.employeeID = employeeRef.current.getResult() || 0;
		}

		return result;
	};

	// 設置錯誤提示
	const setEffectTypeErrorHelperText = (pickItemRef, isError, message) => {
		if (pickItemRef?.current?.setHelperText) {
			pickItemRef.current.setHelperText(isError, message);
		}
	};

	// 清除所有 effectType 錯誤提示
	const clearAllEffectTypeErrors = () => {
		setEffectTypeErrorHelperText(productPickItemRef, false, "");
		setEffectTypeErrorHelperText(shippingPickItemRef, false, "");
		setEffectTypeErrorHelperText(receiverPickItemRef, false, "");
		setEffectTypeErrorHelperText(paymentPickItemRef, false, "");
		setEffectTypeErrorHelperText(invoicePickItemRef, false, "");
	};

	// 驗證
	const checkError = () => {
		// 清空所有錯誤提示
		clearAllEffectTypeErrors();

		// 檢查是否至少選取一個 effectType
		if (!hasAnySelected) {
			setMessageDialog({
				open: true,
				title: "提醒",
				msg: "請選擇至少一個變更項目",
			});
			// 在所有 PickItem 上顯示錯誤
			setEffectTypeErrorHelperText(productPickItemRef, true, "請選擇至少一個變更項目");
			setEffectTypeErrorHelperText(shippingPickItemRef, true, "請選擇至少一個變更項目");
			setEffectTypeErrorHelperText(receiverPickItemRef, true, "請選擇至少一個變更項目");
			setEffectTypeErrorHelperText(paymentPickItemRef, true, "請選擇至少一個變更項目");
			setEffectTypeErrorHelperText(invoicePickItemRef, true, "請選擇至少一個變更項目");
			return true;
		}

		// 檢查必填欄位
		const refsToCheck = [termRef, effectCreateDateRef, effectDateRef, employeeRef];

		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.ITEMS)) {
			if (checkProductError()) return true;
			refsToCheck.push(productItemsRef, productConfirmRef);
		}

		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.SHIPPINGDAY)) {
			refsToCheck.push(shippingDayRef);
		}

		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.RECEIVER)) {
			refsToCheck.push(receiverRef);
		}

		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.PAYMENT)) {
			refsToCheck.push(paymentRef);
		}

		if (isEffectTypeSelected(EFFECT_CONTENT_TYPE.INVOICE)) {
			refsToCheck.push(invoiceRef);
		}

		return refIsRequiredError(...refsToCheck);
	};

	const handleSubmit = () => {
		// 防止重複提交
		if (isSubmittingOrder()) return;

		const isError = checkError();
		if (!isError) {
			const params = getParams();
			submit(params);
		}
	};

	// 當 effectType 變更時，重新計算生效日期
	useEffect(() => {
		if (hasAnySelected) {
			const params = getParams();
			getEffectRangeDebounced(params);
		}
		// eslint-disable-next-line
	}, [selectedEffectTypesArray]);

	return (
		<Box className="container main-container-spacing cycle-purchase-page">
			<Box component="header" className="page-header">
				<Typography className="page-header-title" variant="h3">
					健康產品變更單
				</Typography>
			</Box>
			<FocusModeToggle ref={focusModeRef}>
				<CyclePurchaseSummaryCard sourceData={cycleOrder} />
				<Typography className="font-weight-bold mb-1" variant="h5">
					請選擇要變動的項目
				</Typography>
				<Card className="main-effect-group order-info-card mb-4">
					{/* 商品口味數量 */}
					<Card.CardHeader
						title={
							<PickItem
								title="商品口味數量"
								ref={productPickItemRef}
								checked={isEffectTypeSelected(EFFECT_CONTENT_TYPE.ITEMS)}
								handleChange={checked => handleEffectTypeChange(EFFECT_CONTENT_TYPE.ITEMS, checked)}
							/>
						}
						className="bg-light-active px-4"
					/>
					{isEffectTypeSelected(EFFECT_CONTENT_TYPE.ITEMS) && (
						<Card.CardContent className="px-4 pt-2 pb-4">
							<CartTable ref={productItemsRef} key={defaultSource?.items} sourceData={defaultSource?.items ?? []} className="mb-4" resource={products} />
							<Stack direction="column" className="bg-info-light px-4 py-2 mb-4">
								<CheckboxField ref={productConfirmRef} required>
									<CheckboxField.Checkbox label="我同意於提出變更口味申請後，刷卡金額會依品項單價而不同。" value={true} required />
								</CheckboxField>
							</Stack>
						</Card.CardContent>
					)}

					{/* 出貨週期 */}
					<Card.CardHeader
						title={
							<PickItem
								title="出貨週期"
								ref={shippingPickItemRef}
								checked={isEffectTypeSelected(EFFECT_CONTENT_TYPE.SHIPPINGDAY)}
								handleChange={checked => handleEffectTypeChange(EFFECT_CONTENT_TYPE.SHIPPINGDAY, checked)}
							/>
						}
						className="bg-light-active px-4"
					/>
					{isEffectTypeSelected(EFFECT_CONTENT_TYPE.SHIPPINGDAY) && (
						<Card.CardContent className="p-4">
							<Grid spacing={2} container>
								<Grid xs={4} item>
									<SelectWithMenuItems
										label="出貨週期"
										ref={shippingDayRef}
										defaultValue={defaultSource?.shippingDay ?? cycleOrder?.shippingDay}
										options={resource?.shippingDayOptions ?? []}
										onChange={handleShippingDayChange}
										fullWidth
										required
									/>
								</Grid>
							</Grid>
						</Card.CardContent>
					)}

					{/* 收件資訊 */}
					<ReceiverSection
						ref={receiverRef}
						initialSource={defaultSource || cycleOrder || {}}
						memberID={memberID}
						isSelected={isEffectTypeSelected(EFFECT_CONTENT_TYPE.RECEIVER)}
						onEffectTypeChange={checked => handleEffectTypeChange(EFFECT_CONTENT_TYPE.RECEIVER, checked)}
						pickItemRef={receiverPickItemRef}
					/>

					{/* 付款資訊 */}
					<PaymentSection
						ref={paymentRef}
						memberID={memberID}
						initialSource={defaultSource?.payment || cycleOrder?.payment || {}}
						cycleOrder={cycleOrder}
						errorModule={validationErrors}
						isSelected={isEffectTypeSelected(EFFECT_CONTENT_TYPE.PAYMENT)}
						onEffectTypeChange={checked => handleEffectTypeChange(EFFECT_CONTENT_TYPE.PAYMENT, checked)}
						pickItemRef={paymentPickItemRef}
						isShowNoDeductNow
					/>

					{/* 發票資訊 */}
					<InvoiceSection
						ref={invoiceRef}
						memberID={memberID}
						initialSource={defaultSource?.invoice || cycleOrder?.invoice || {}}
						resource={resource}
						errorModule={validationErrors}
						isSelected={isEffectTypeSelected(EFFECT_CONTENT_TYPE.INVOICE)}
						onEffectTypeChange={checked => handleEffectTypeChange(EFFECT_CONTENT_TYPE.INVOICE, checked)}
						pickItemRef={invoicePickItemRef}
					/>
				</Card>
				<TermSection className="mb-4" ref={termRef} nextShippingDate={hasAnySelected && nextShippingDate} errorModule={validationErrors} showPriceNotice={isPriceNoticePeriod()} />
				<Card className="order-info-card mb-4">
					<Card.CardContent className={"p-4"}>
						<Grid spacing={2} container>
							<Grid xs={4} item>
								<DateField
									label="建單日期"
									ref={effectCreateDateRef}
									key={initialDate(effectOrder?.effectCreateDate)}
									defaultValue={initialDate(effectOrder?.effectCreateDate) || initialDate(getNowTime())}
									{...getErrorModuleStatus(validationErrors, "effectCreateDate")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<DateField
									label="變更生效日期"
									ref={effectDateRef}
									key={initialDate(effectDate)}
									defaultValue={initialDate(effectDate)}
									onChange={handleEffectDateChange}
									{...getErrorModuleStatus(validationErrors, "effectDate")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<EmployeeSelect label="擔當" ref={employeeRef} options={resource?.coachOptions ?? []} {...getErrorModuleStatus(validationErrors, "employeeID")} fullWidth required />
							</Grid>
						</Grid>
					</Card.CardContent>
					<Box className="text-right px-4 pb-3">
						<Button variant="contained" onClick={() => focusModeRef.current.checkAndExitFocusMode(handleSubmit)}>
							確認送出
						</Button>
					</Box>
				</Card>
			</FocusModeToggle>
		</Box>
	);
};

export default CyclePurchaseChangePage;
